import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { Editor } from "@tinymce/tinymce-react";

const AddNews = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [newsList, setNewsList] = useState([]);

  // 🔹 تحميل المقالات من Firestore
  useEffect(() => {
    const q = query(collection(db, "news"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNewsList(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return unsubscribe;
  }, []);

  // 🔹 رفع الصورة إلى Cloudinary
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setImage("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "iisdweb");
    formData.append("cloud_name", "dys8jv8c2");

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/dys8jv8c2/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      setImage(data.secure_url);
    } catch (error) {
      console.error("❌ خطأ في رفع الصورة:", error);
      alert("حدث خطأ أثناء رفع الصورة");
    } finally {
      setUploading(false);
    }
  };

  // 🔹 حفظ المقال في Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !image) {
      alert("من فضلك أكمل جميع الحقول بعد رفع الصورة");
      return;
    }

    try {
      await addDoc(collection(db, "news"), {
        title: title.trim(),
        content,
        image,
        createdAt: serverTimestamp(),
      });

      alert("✅ تم إضافة المقال بنجاح!");
      setTitle("");
      setContent("");
      setImage("");
    } catch (error) {
      console.error("❌ خطأ أثناء الحفظ:", error);
      alert("حدث خطأ أثناء حفظ المقال");
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>📰 إضافة مقال جديد</h2>

      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>عنوان المقال:</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={styles.input}
          placeholder="اكتب عنوان المقال هنا"
        />

        <label style={styles.label}>محتوى المقال:</label>
        <Editor
          apiKey="ed5gqe3xen6ohca7sw4s4fozeudblp8882cth23oc8nso2ue" // 🔹 ضع مفتاح TinyMCE هنا (أو استخدم بدون مفتاح)
          value={content}
          init={{
            height: 300,
            menubar: false,
            directionality: "rtl",
            language: "ar",
            plugins: [
              "advlist autolink lists link image charmap preview anchor",
              "searchreplace visualblocks code fullscreen",
              "insertdatetime media table code help wordcount",
            ],
            toolbar:
              "undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | link image | preview",
            content_style:
              "body { font-family: Cairo, sans-serif; font-size: 16px; direction: rtl; text-align: right; }",
          }}
          onEditorChange={(newValue) => setContent(newValue)}
        />

        <label style={styles.label}>صورة المقال:</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={styles.fileInput}
        />

        {uploading && <p style={styles.uploading}>⏳ جاري رفع الصورة...</p>}
        {image && (
          <div style={styles.previewWrapper}>
            <img src={image} alt="Preview" style={styles.previewImage} />
          </div>
        )}

        <button
          type="submit"
          style={{ ...styles.button, opacity: uploading ? 0.6 : 1 }}
          disabled={uploading}
        >
          {uploading ? "⏳ جاري رفع الصورة..." : "إضافة المقال"}
        </button>
      </form>

      {/* 🔹 جدول المقالات */}
      <h3 style={{ marginTop: "40px", color: "#004aad" }}>🗂️ قائمة المقالات الحالية</h3>
      <table style={styles.table}>
        <thead>
          <tr>
            <th>العنوان</th>
            <th>الصورة</th>
            <th>تاريخ الإضافة</th>
          </tr>
        </thead>
        <tbody>
          {newsList.map((item) => (
            <tr key={item.id}>
              <td>{item.title}</td>
              <td>
                <img
                  src={item.image}
                  alt={item.title}
                  style={{ width: "80px", borderRadius: "8px" }}
                />
              </td>
              <td>
                {item.createdAt
                  ? new Date(item.createdAt.seconds * 1000).toLocaleString("ar-EG")
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// 💅 التنسيقات
const styles = {
  container: {
    maxWidth: "900px",
    margin: "50px auto",
    backgroundColor: "#fff",
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
    direction: "rtl",
    fontFamily: "Cairo, sans-serif",
  },
  title: {
    textAlign: "center",
    color: "#004aad",
    fontSize: "1.8rem",
    fontWeight: "700",
    marginBottom: "25px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  label: {
    fontWeight: "600",
    color: "#222",
  },
  input: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  },
  fileInput: {
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "10px",
  },
  previewWrapper: {
    textAlign: "center",
    marginTop: "10px",
  },
  previewImage: {
    maxWidth: "100%",
    borderRadius: "10px",
  },
  uploading: {
    color: "#004aad",
    fontWeight: "600",
  },
  button: {
    backgroundColor: "#004aad",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "1.1rem",
    fontWeight: "600",
    marginTop: "10px",
    transition: "0.3s",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "20px",
    textAlign: "center",
  },
};

export default AddNews;
